import { EventBus } from './event-bus'
import { ReconnectionStrategy } from './reconnection-strategy'
import type { ConnectionState, ConnectionStatus, RealtimeEvent } from './types'

const STABLE_CONNECTION_MS = 5000

export class RealtimeClient {
  private eventSource: EventSource | null = null
  private eventBus = new EventBus()
  private reconnectionStrategy: ReconnectionStrategy
  private reconnectTimeout: NodeJS.Timeout | null = null
  private state: ConnectionState = 'disconnected'
  private statusListeners: Set<(status: ConnectionStatus) => void> = new Set()
  private endpoint: string
  private shouldReconnect = true
  private connectedAt: number | null = null
  private handlingError = false

  constructor(endpoint: string = '/api/events/stream') {
    this.endpoint = endpoint
    this.reconnectionStrategy = new ReconnectionStrategy()
  }

  getState(): ConnectionState {
    return this.state
  }

  onStatusChange(listener: (status: ConnectionStatus) => void): () => void {
    this.statusListeners.add(listener)
    return () => this.statusListeners.delete(listener)
  }

  private updateState(state: ConnectionState, error?: Error): void {
    if (this.state !== state) {
      console.log('[SSE] State changed:', this.state, '→', state)
    }
    this.state = state
    this.emitStatus(state, error)
  }

  private emitStatus(state: ConnectionState, error?: Error): void {
    const status: ConnectionStatus = {
      state,
      error,
      reconnectAttempt: this.reconnectionStrategy.getAttempt(),
    }
    this.statusListeners.forEach((listener) => {
      try {
        listener(status)
      } catch (err) {
        console.error('Error in status listener:', err)
      }
    })
  }

  connect(): void {
    if (typeof window === 'undefined') return
    this.shouldReconnect = true

    if (this.state === 'connecting' || this.state === 'connected') {
      return
    }

    this.handlingError = false
    this.updateState('connecting')
    console.log('[SSE] Connecting to', this.endpoint)

    try {
      this.eventSource = new EventSource(this.endpoint)

      this.eventSource.onopen = () => {
        this.connectedAt = Date.now()
        this.handlingError = false
        console.log('[SSE] Event stream connected')
        this.updateState('connected')
      }

      this.eventSource.onmessage = (event) => {
        this.handleMessage(event.data)
      }

      this.eventSource.onerror = () => {
        this.handleError()
      }

      this.eventSource.addEventListener('file_moved', (event) => {
        this.handleMessage((event as MessageEvent).data)
      })

      this.eventSource.addEventListener('upload_completed', (event) => {
        this.handleMessage((event as MessageEvent).data)
      })

      this.eventSource.addEventListener('share_updated', (event) => {
        this.handleMessage((event as MessageEvent).data)
      })

      this.eventSource.addEventListener('trash_updated', (event) => {
        this.handleMessage((event as MessageEvent).data)
      })
    } catch (error) {
      this.handleError(error as Error)
    }
  }

  private handleMessage(data: string): void {
    try {
      const event: RealtimeEvent = JSON.parse(data)
      console.log('[SSE] Event received:', event.type, event.data)
      this.eventBus.emit(event)
    } catch (error) {
      console.error('[SSE] Failed to parse event:', error, 'data:', data)
    }
  }

  private closeEventSource(): void {
    if (!this.eventSource) return
    this.eventSource.onopen = null
    this.eventSource.onmessage = null
    this.eventSource.onerror = null
    this.eventSource.close()
    this.eventSource = null
  }

  private handleError(error?: Error): void {
    if (!this.shouldReconnect || this.handlingError) {
      return
    }
    this.handlingError = true

    const wasStable =
      this.connectedAt != null && Date.now() - this.connectedAt >= STABLE_CONNECTION_MS
    if (wasStable) {
      this.reconnectionStrategy.reset()
    }
    this.connectedAt = null

    console.log('[SSE] Event stream disconnected', error?.message || '')
    this.closeEventSource()
    this.updateState('error', error)

    if (!this.shouldReconnect) {
      this.updateState('disconnected')
      return
    }

    const delay = this.reconnectionStrategy.getDelay()
    if (delay !== null) {
      console.log(
        `[SSE] Reconnecting in ${Math.round(delay)}ms (attempt ${this.reconnectionStrategy.getAttempt()}/10)`,
      )
      this.reconnectTimeout = setTimeout(() => {
        this.reconnectTimeout = null
        this.handlingError = false
        if (this.shouldReconnect) this.connect()
      }, delay)
    } else {
      this.shouldReconnect = false
      this.updateState('disconnected')
      console.log('[SSE] Event stream disconnected — max reconnection attempts reached')
    }
  }

  on(eventType: string | '*', handler: (event: RealtimeEvent) => void): () => void {
    this.eventBus.on(eventType as any, handler)
    return () => this.eventBus.off(eventType as any, handler)
  }

  close(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }
    this.closeEventSource()
    this.connectedAt = null
    this.handlingError = false
    if (this.state !== 'disconnected') {
      this.updateState('disconnected')
    }
  }

  disconnect(): void {
    console.log('[SSE] Disconnecting')
    this.shouldReconnect = false
    this.close()
    this.reconnectionStrategy = new ReconnectionStrategy()
  }
}
